import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";

import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";
import heritageHero from "@/assets/heritage-hero.jpg.asset.json";
import heritageMood1 from "@/assets/heritage-mood-1.jpg.asset.json";
import heritageMood2 from "@/assets/heritage-mood-2.jpg.asset.json";
import heritageMood3 from "@/assets/heritage-mood-3.jpg.asset.json";


const PATH = "/heritage";

const TRACKS = [
  {
    eyebrow: "För familjer och privatpersoner",
    title: "Livsberättelser",
    body:
      "En filmad intervju där en person får berätta om sitt liv, sin uppväxt, familj, arbete, erfarenheter och viktiga minnen. Filmen bevaras för barn, barnbarn och kommande generationer.",
    to: "/heritage/livsberattelser",
    cta: "Läs mer om livsberättelser",
  },
  {
    eyebrow: "För företag och organisationer",
    title: "Kunskapsarv",
    body:
      "En strukturerad filmad intervju eller intervjuserie med nyckelpersoner, grundare, seniora medarbetare eller personer som bär på viktig erfarenhet. Materialet används för onboarding, intern utbildning, kulturarbete eller dokumentation.",
    to: "/heritage/kunskapsarv",
    cta: "Läs mer om kunskapsarv",
  },
];

const HERITAGE_TOPIC_LINKS = [
  { group: "private", to: "/heritage/livsberattelse-till-foralder", label: "Livsberättelse till en förälder" },
  { group: "private", to: "/heritage/slakthistoria-pa-film", label: "Släkthistoria på film" },
  { group: "private", to: "/heritage/minnesfilm", label: "Minnesfilm" },
  { group: "private", to: "/heritage/intervjufragor-livsberattelse", label: "Intervjufrågor till en livsberättelse" },
  { group: "business", to: "/heritage/generationsskifte-dokumentation", label: "Dokumentation vid generationsskifte" },
  { group: "business", to: "/heritage/pensionsavgang-kunskapsoverforing", label: "Kunskapsöverföring inför pension" },
  { group: "business", to: "/heritage/foretagshistoria-pa-film", label: "Företagshistoria på film" },
  { group: "business", to: "/heritage/tyst-kunskap-dokumentation", label: "Dokumentera tyst kunskap" },
] as const;

const HERITAGE_CITY_LINKS = [
  { to: "/heritage/stockholm", label: "Stockholm" },
  { to: "/heritage/goteborg", label: "Göteborg" },
  { to: "/heritage/malmo", label: "Malmö" },
  { to: "/heritage/uppsala", label: "Uppsala" },
  { to: "/heritage/vasteras", label: "Västerås" },
  { to: "/heritage/linkoping", label: "Linköping" },
  { to: "/heritage/norrkoping", label: "Norrköping" },
  { to: "/heritage/orebro", label: "Örebro" },
  { to: "/heritage/jonkoping", label: "Jönköping" },
  { to: "/heritage/helsingborg", label: "Helsingborg" },
  { to: "/heritage/lund", label: "Lund" },
  { to: "/heritage/umea", label: "Umeå" },
] as const;

const PROCESS = [
  {
    label: "Första samtal",
    body:
      "Vi börjar med att förstå syftet, vem som ska intervjuas och hur materialet ska användas.",
  },
  {
    label: "Planering och frågeunderlag",
    body:
      "Vi tar fram ett upplägg, frågeområden och en struktur som gör intervjun tydlig och trygg.",
  },
  {
    label: "Filmad intervju",
    body:
      "Intervjun genomförs i en lugn miljö där personen får berätta i sin egen takt.",
  },
  {
    label: "Klippning och bearbetning",
    body:
      "Materialet klipps till en färdig film eller flera kortare delar. Ljud, färg och struktur bearbetas professionellt.",
  },
  {
    label: "Leverans",
    body:
      "Filmen levereras digitalt, redo att delas privat inom familjen eller användas internt i organisationen.",
  },
];

const USE_CASES_PERSONAL = [
  "Intervju med förälder eller morförälder",
  "Släkthistoria",
  "Personligt porträtt",
  "Minnesfilm",
  "Filmad hälsning till familjen",
  "Dokumentation inför framtiden",
];

const USE_CASES_BUSINESS = [
  "Kunskapsöverföring inför pension",
  "Intervju med grundare",
  "Dokumentation av företagskultur",
  "Onboardingmaterial",
  "Intern utbildning",
  "Generationsskifte",
  "Företagshistoria",
  "Dokumentation av arbetssätt och erfarenhet",
];

const PACKAGES_PERSONAL = [
  {
    name: "Livsintervjun",
    body:
      "Ett mindre upplägg för en filmad intervju med en person. Förmöte, frågeunderlag, filmad intervju, klippning och digital leverans.",
  },
  {
    name: "Familjearvet",
    body:
      "Ett mer omfattande upplägg med längre intervju, kapitelindelning och möjlighet att inkludera bilder eller arkivmaterial.",
  },
  {
    name: "Legacy",
    body:
      "För familjer som vill skapa ett mer omfattande filmat arv med flera intervjuer, tydlig berättelsestruktur, arkivmaterial, huvudfilm och kortare klipp.",
  },
];

const PACKAGES_BUSINESS = [
  {
    name: "Key Person Interview",
    body:
      "För en senior medarbetare, specialist, ledare eller grundare. Förmöte, intervjuupplägg, filmad intervju, klippning och intern leverans.",
  },
  {
    name: "Knowledge Transfer",
    body:
      "För organisationer som behöver dokumentera tyst kunskap, arbetssätt och erfarenhet. Filmad intervju eller intervjuserie med kapitelindelat material för onboarding och internutbildning.",
  },
  {
    name: "Company Legacy",
    body:
      "För företag som vill dokumentera historia, kultur, grundarresa eller generationsskifte. Intervjuer med flera personer, sammanfattande film och kortare interna klipp.",
  },
];

const VALUE_PERSONAL = [
  "Bevarar berättelser direkt från personen själv",
  "Gör det möjligt för kommande generationer att ta del av rösten, minnena och berättelsen",
  "Skapar ett mer levande alternativ till text, foto eller släktforskning",
  "Kan delas privat inom familjen",
  "Gör det lättare att ställa frågor medan det fortfarande finns tid",
];

const VALUE_BUSINESS = [
  "Minskar risken att viktig erfarenhet försvinner när personer lämnar organisationen",
  "Underlättar onboarding och intern utbildning",
  "Dokumenterar kultur, värderingar och arbetssätt",
  "Bevarar grundarhistoria och företagsminne",
  "Kan användas vid generationsskiften, pensionsavgångar och jubileum",
  "Gör tyst kunskap mer tillgänglig för fler i organisationen",
];

const FAQ = [
  {
    q: "Vad är Fenny Heritage?",
    a: "Fenny Heritage är en tjänst för filmade intervjuer där privatpersoner, familjer, företag och organisationer kan dokumentera livsberättelser, erfarenhet och kunskap i professionell form.",
  },
  {
    q: "Vem passar tjänsten för?",
    a: "Familjer som vill bevara en närståendes berättelse, och företag eller organisationer som vill dokumentera erfarenhet, företagshistoria, kultur och tyst kunskap.",
  },
  {
    q: "Vad är skillnaden mellan livsberättelser och kunskapsarv?",
    a: "Livsberättelser är privata intervjuer om en persons liv, avsedda för familjen. Kunskapsarv är strukturerade intervjuer i organisationer för att dokumentera erfarenhet, arbetssätt och företagshistoria.",
  },
  {
    q: "Hur lång blir filmen?",
    a: "Det beror på upplägg och syfte. En enskild huvudfilm är ofta 15–45 minuter, och kompletteras gärna med kortare utdrag eller kapitel som är lättare att dela och titta på.",
  },
  {
    q: "Var genomförs intervjun?",
    a: "I en lugn miljö som passar personen — hemma, på kontoret, i en studio eller på en plats med personlig betydelse. Vi tar med all utrustning och bygger upp en trygg inspelningsmiljö.",
  },
  {
    q: "Kan vi bidra med egna frågor?",
    a: "Ja. Frågeunderlaget tas fram tillsammans. Vi föreslår struktur och områden, ni bidrar med det som är viktigt för er, och personen som intervjuas får se frågorna i förväg om så önskas.",
  },
  {
    q: "Kan ni använda gamla bilder eller arkivmaterial?",
    a: "Ja. I de större paketen kan vi väva in foton, dokument, film eller annat arkivmaterial som kompletterar berättelsen.",
  },
  {
    q: "Kan materialet hållas privat?",
    a: "Ja. Filmen levereras digitalt direkt till er och delas inte vidare. Ni bestämmer själva vem som får se den.",
  },
  {
    q: "Kan företag använda filmerna internt?",
    a: "Ja. Företagspaketen är avsedda för intern användning — onboarding, utbildning, kulturarbete, jubileum eller intranät. Extern publicering går också att ordna när det passar.",
  },
  {
    q: "Hur fungerar samtycke och rättigheter?",
    a: "Personen som intervjuas ger skriftligt samtycke till inspelning och användningsområde. Rättigheterna till slutfilmen tillhör er. Vi följer god dokumentärsed och ger personen möjlighet att se materialet före leverans.",
  },
  {
    q: "Hur lång tid tar produktionen?",
    a: "Från första samtal till färdig film tar processen oftast 4–8 veckor, beroende på upplägg, antal intervjuer och hur mycket arkivmaterial som ska bearbetas.",
  },
  {
    q: "Vad kostar Fenny Heritage?",
    a: "Priset beror på upplägg och omfattning. Vi lämnar offert efter första samtalet. Mindre intervjuupplägg startar i den lägre prisklassen för Prime-produktioner, mer omfattande arv ligger högre.",
  },
];

export const Route = createFileRoute("/heritage/")({
  head: () => ({
    meta: [
      { title: "Fenny Heritage, filmade livsberättelser och kunskapsarv" },
      {
        name: "description",
        content:
          "Fenny Heritage hjälper familjer och företag att bevara livsberättelser, erfarenhet och tyst kunskap genom professionellt filmade intervjuer.",
      },
      { property: "og:title", content: "Fenny Heritage — livsberättelser och kunskapsarv på film" },
      {
        property: "og:description",
        content:
          "Filmade intervjuer för familjer, företag och organisationer. Bevara berättelser, erfarenhet och kunskap som inte får gå förlorade.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [
      { rel: "canonical", href: `https://fenny.se${PATH}` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/heritage", name: "Fenny Heritage, filmade livsberättelser och kunskapsarv", description: "Fenny Heritage hjälper familjer och företag att bevara livsberättelser, erfarenhet och tyst kunskap genom professionellt filmade intervjuer."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Fenny Heritage",
            path: PATH,
            description:
              "Filmade intervjuer för livsberättelser och kunskapsarv. Fenny hjälper familjer, företag och organisationer att dokumentera berättelser, erfarenhet och tyst kunskap.",
            serviceType: "Filmad intervju och dokumentation",
            priceRange: "Offert efter första samtal",
            areaServed: ["Sweden"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Heritage", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: HeritageHub,
});

function HeritageHub() {
  return (
    <article>
      {/* Hero with cover image + fade */}
      <section className="relative isolate overflow-hidden border-b border-white/5 pt-44 pb-20 md:pt-56 md:pb-28">
        <img
          src={heritageHero.url}
          alt="Heritage-inspelning av Fenny — livsberättelse dokumenterad på film i personens egen miljö"
          fetchPriority="high"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-[70%_center] md:object-[65%_center]"
        />
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(100deg, color-mix(in oklab, var(--background) 94%, transparent) 0%, color-mix(in oklab, var(--background) 80%, transparent) 45%, color-mix(in oklab, var(--background) 38%, transparent) 80%, transparent 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 -z-10 h-40"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, var(--background) 100%)",
          }}
        />
        <div className="container-wide">
          <div className="max-w-4xl">
            <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">
              Fenny Heritage
            </div>
            <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
              Berättelser och kunskap som inte får gå förlorade
            </h1>
            <p
              className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl"
              data-speakable
            >
              Fenny Heritage hjälper familjer, företag och organisationer att
              dokumentera livsberättelser, erfarenhet och tyst kunskap genom
              professionellt filmade intervjuer.
            </p>
            <QuickAnswer
              className="mt-6"
              text="Fenny Heritage är en tjänst för filmade intervjuer där privatpersoner, familjer, företag och organisationer kan dokumentera livsberättelser, erfarenhet och kunskap i professionell form."
            />
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-3 text-sm font-medium text-background hover:opacity-90"
              >
                Boka ett första samtal <ArrowRight className="size-4" />
              </Link>
              <a
                href="#process"
                className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-3 text-sm font-medium hover:bg-foreground/[0.05]"
              >
                Se hur det fungerar
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage-film — editorial split */}
      <Section className="py-20 md:py-28">
        <div className="grid gap-10 md:grid-cols-12 md:gap-12">
          {/* Left column — editorial copy */}
          <div className="md:col-span-4 md:sticky md:top-28 md:self-start">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-mint">
            <span className="font-mono text-[11px] tabular-nums text-mint/60">03 / urval</span>
              <span aria-hidden className="h-px flex-1 bg-mint/30" />
            </div>
            <div className="mt-5 text-xs uppercase tracking-[0.22em] text-mint">
              Se Heritage i rörelse
            </div>
            <h2 className="mt-4 text-balance text-4xl font-semibold leading-[1.05] md:text-5xl">
              Hur en <em className="font-serif italic text-mint/90">heritage-film</em> kan se ut
            </h2>
            <p className="mt-6 max-w-md text-pretty text-foreground/75">
              Tre utdrag ur arkivet — så vi fångar berättelser i lugn miljö,
              med personen i centrum, utan stress och utan iscensättning.
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-white/10 pt-6 text-sm">
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-foreground/50">Längd</dt>
                <dd className="mt-1 font-medium tabular-nums">15–45 min</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-foreground/50">Format</dt>
                <dd className="mt-1 font-medium">Filmad intervju</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-foreground/50">Ton</dt>
                <dd className="mt-1 font-medium">Stillsam, närvarande</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-foreground/50">Spår</dt>
                <dd className="mt-1 font-medium">Familj &amp; företag</dd>
              </div>
            </dl>
          </div>

          {/* Right column — cinematic video frame */}
          <div className="md:col-span-8">
            <figure className="group relative">
              {/* Decorative offset frame */}
              <div
                aria-hidden
                className="absolute -inset-3 -z-10 rounded-[28px] bg-gradient-to-br from-mint/15 via-mint/[0.04] to-transparent opacity-70 blur-2xl transition-opacity duration-700 group-hover:opacity-100"
              />
              <div
                aria-hidden
                className="absolute -left-4 -top-4 -z-10 h-24 w-24 rounded-full border border-mint/30"
              />
              <div
                aria-hidden
                className="absolute -bottom-4 -right-4 -z-10 hidden h-32 w-32 rounded-2xl border border-white/10 md:block"
              />

              {/* Corner ticks */}
              <span aria-hidden className="pointer-events-none absolute -left-2 -top-2 z-10 h-5 w-5 border-l border-t border-mint/60" />
              <span aria-hidden className="pointer-events-none absolute -right-2 -top-2 z-10 h-5 w-5 border-r border-t border-mint/60" />
              <span aria-hidden className="pointer-events-none absolute -bottom-2 -left-2 z-10 h-5 w-5 border-b border-l border-mint/60" />
              <span aria-hidden className="pointer-events-none absolute -bottom-2 -right-2 z-10 h-5 w-5 border-b border-r border-mint/60" />

              <div className="relative aspect-video overflow-hidden rounded-2xl ring-1 ring-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] bg-black">
                <iframe
                  title="Fenny Heritage — referensfilm 1"
                  src="https://www.youtube-nocookie.com/embed/VEd7zXA30J0?autoplay=1&mute=1&loop=1&playlist=VEd7zXA30J0&controls=1&rel=0&modestbranding=1&playsinline=1"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full border-0"
                />
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {[
                  { id: "KkE8BqgQ14M", title: "Fenny Heritage — referensfilm 2" },
                  { id: "2YCWQZnks5Q", title: "Fenny Heritage — referensfilm 3" },
                ].map((v) => (
                  <div
                    key={v.id}
                    className="relative aspect-video overflow-hidden rounded-xl ring-1 ring-white/10 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.7)] bg-black"
                  >
                    <iframe
                      title={v.title}
                      src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&mute=1&loop=1&playlist=${v.id}&controls=1&rel=0&modestbranding=1&playsinline=1`}
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                      className="absolute inset-0 h-full w-full border-0"
                    />
                  </div>
                ))}
              </div>

              {/* Caption strip */}
              <figcaption className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4 text-xs uppercase tracking-[0.18em] text-foreground/60">
                <span className="flex items-center gap-2">
                  <span className="inline-block size-1.5 rounded-full bg-mint" />
                  Ur arkivet · Heritage
                </span>
                <span className="font-mono tabular-nums text-foreground/50">
                  Ett urval · 2026
                </span>
              </figcaption>
            </figure>
          </div>
        </div>
      </Section>



      {/* Kort förklaring + koppling till Fenny */}
      <Section className="py-8">
        <div className="max-w-3xl text-pretty text-base text-foreground/80 md:text-lg">
          <p>
            Det finns berättelser, erfarenheter och kunskap som inte går att
            återskapa i efterhand. Heritage samlar två närliggande behov:
            livsberättelser för familjer och kunskapsarv för organisationer.
            Den gemensamma kärnan är densamma — strukturerade, filmade intervjuer
            med hantverk och respekt.
          </p>
          <p className="mt-4 text-foreground/70">
            Där Fenny hjälper företag att berätta framåt, hjälper Fenny Heritage
            människor och organisationer att bevara det som format dem.
          </p>
        </div>
      </Section>

      {/* Känslobilder — stills som visar tonen */}
      <Section className="py-10">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-mint">
              Tonen i en heritage-film
            </div>
            <h2 className="mt-2 text-balance text-2xl font-semibold md:text-3xl">
              Stillsamt, naturligt, närvarande
            </h2>
          </div>
          <p className="hidden max-w-md text-sm text-foreground/70 md:block">
            Stills från en pågående produktion. Vi filmar i personens egen
            miljö — utan stress, utan iscensättning.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[heritageMood1.url, heritageMood2.url, heritageMood3.url].map((src, i) => (
            <figure
              key={i}
              className="group relative aspect-[16/9] overflow-hidden rounded-2xl ring-1 ring-white/5"
            >
              <img
                src={src}
                alt={`Stillbild från en Heritage-produktion av Fenny (bild ${i + 1} av 3)`}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
              />
            </figure>
          ))}
        </div>
      </Section>

      {/* Två spår */}
      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Två spår</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {TRACKS.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              className="group flex flex-col rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-7 transition hover:border-foreground/30 hover:bg-foreground/[0.06]"
            >
              <div className="text-xs uppercase tracking-widest text-mint">{t.eyebrow}</div>
              <h3 className="mt-3 text-2xl font-semibold md:text-3xl">{t.title}</h3>
              <p className="mt-3 text-pretty text-foreground/80">{t.body}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-mint">
                {t.cta}
                <ArrowRight className="size-4 transition group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      {/* Fördjupning — ämnessidor per behov */}
      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Fördjupning per behov</h2>
        <p className="mt-4 max-w-2xl text-pretty text-foreground/80">
          Läs mer om det som ligger närmast er situation — oavsett om det gäller
          en förälders livsberättelse eller kunskap som riskerar att försvinna
          när en nyckelperson slutar.
        </p>
        <div className="mt-8 grid gap-8 md:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-mint">
              Familjer och privatpersoner
            </div>
            <ul className="mt-4 space-y-2">
              {HERITAGE_TOPIC_LINKS.filter((t) => t.group === "private").map((t) => (
                <li key={t.to}>
                  <Link
                    to={t.to}
                    className="inline-flex items-center gap-2 text-foreground/85 underline decoration-mint/40 underline-offset-4 hover:text-mint"
                  >
                    {t.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-mint">
              Företag och organisationer
            </div>
            <ul className="mt-4 space-y-2">
              {HERITAGE_TOPIC_LINKS.filter((t) => t.group === "business").map((t) => (
                <li key={t.to}>
                  <Link
                    to={t.to}
                    className="inline-flex items-center gap-2 text-foreground/85 underline decoration-mint/40 underline-offset-4 hover:text-mint"
                  >
                    {t.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-mint">Heritage lokalt</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {HERITAGE_CITY_LINKS.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="rounded-full border border-foreground/15 px-4 py-2 text-sm text-foreground/85 hover:border-mint/50 hover:text-mint"
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      </Section>



      {/* Varför tjänsten finns — saklig värdesektion */}
      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Varför Heritage finns</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-surface/40 p-6">
            <div className="text-xs uppercase tracking-widest text-mint">För privatpersoner</div>
            <ul className="mt-4 space-y-3 text-foreground/85">
              {VALUE_PERSONAL.map((v) => (
                <li key={v} className="flex gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-mint" />
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-surface/40 p-6">
            <div className="text-xs uppercase tracking-widest text-mint">För företag</div>
            <ul className="mt-4 space-y-3 text-foreground/85">
              {VALUE_BUSINESS.map((v) => (
                <li key={v} className="flex gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-mint" />
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Process */}
      <Section className="py-10" id="process">
        <h2 className="text-3xl font-semibold md:text-4xl">Så går det till</h2>
        <ol className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {PROCESS.map((s, i) => (
            <li key={s.label} className="rounded-2xl border border-white/10 bg-surface/40 p-5">
              <div className="text-xs uppercase tracking-widest text-mint">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-2 font-medium">{s.label}</div>
              <p className="mt-2 text-sm text-foreground/75">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* Exempel på användningsområden */}
      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Exempel på användningsområden</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-mint">Livsberättelser</div>
            <ul className="mt-4 space-y-2 text-foreground/85">
              {USE_CASES_PERSONAL.map((u) => (
                <li key={u}>— {u}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-mint">Kunskapsarv</div>
            <ul className="mt-4 space-y-2 text-foreground/85">
              {USE_CASES_BUSINESS.map((u) => (
                <li key={u}>— {u}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Paketering */}
      <Section className="py-10" id="paket">
        <h2 className="text-3xl font-semibold md:text-4xl">Paket och upplägg</h2>
        <p className="mt-4 max-w-3xl text-foreground/75">
          Två linjer — Heritage Personal för privatpersoner och familjer, Heritage
          Business för företag och organisationer. Priser lämnas som offert efter
          första samtalet.
        </p>

        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-mint">Heritage Personal</div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {PACKAGES_PERSONAL.map((p) => (
              <div key={p.name} className="rounded-2xl border border-white/10 bg-surface/40 p-6">
                <div className="font-medium">{p.name}</div>
                <p className="mt-3 text-sm text-foreground/75">{p.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-mint">Heritage Business</div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {PACKAGES_BUSINESS.map((p) => (
              <div key={p.name} className="rounded-2xl border border-white/10 bg-surface/40 p-6">
                <div className="font-medium">{p.name}</div>
                <p className="mt-3 text-sm text-foreground/75">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Vanliga frågor</h2>
        <div className="mt-8 max-w-3xl space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/75">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      

      <SignatureCta />
    </article>
  );
}
